/**
 * 피드백 품질 필터링 및 검증 유틸리티
 * 스팸, 부적절한 내용, 품질 낮은 피드백을 필터링
 */

// 금지 단어 목록 (욕설, 스팸성 단어 등)
const BANNED_WORDS = [
  // 욕설 및 비속어
  '개새끼', '병신', '미친', '씨발', '좆', '존나', '개소리', '헛소리',
  '바보', '멍청이', '등신', '놈', '년', '새끼',
  // 스팸성 단어
  '홍보', '광고', '마케팅', '판매', '구매', '할인', '이벤트', '무료', 
  '돈벌기', '부업', '투자', '대출', '카지노', '도박',
  // 반복성 패턴
  'ㅋㅋㅋㅋㅋㅋ', 'ㅎㅎㅎㅎㅎㅎ', 'ㅠㅠㅠㅠㅠㅠ'
];

// 스팸 패턴 (정규식)
const SPAM_PATTERNS = [
  /(.)\1{4,}/g,           // 같은 글자 5회 이상 반복
  /[ㅋㅎㅠㅜㅡ]{5,}/g,     // 자음/모음 5회 이상 연속
  /[!@#$%^&*]{3,}/g,      // 특수문자 3회 이상 연속
  /\d{4,}/g,              // 숫자 4개 이상 연속 (전화번호 등)
  /(http|www|\.com|\.kr)/gi, // URL 패턴
];

// 의미있는 피드백의 최소 요구사항
const MIN_MEANINGFUL_LENGTH = 5;
const MAX_FEEDBACK_LENGTH = 500;

/**
 * 텍스트 품질 점수 계산
 * @param {string} text - 검증할 텍스트
 * @returns {object} 품질 점수와 세부 정보
 */
export function calculateQualityScore(text) {
  if (!text || typeof text !== 'string') {
    return {
      score: 0,
      issues: ['텍스트가 비어있습니다.'],
      category: 'invalid'
    };
  }

  const cleanText = text.trim();
  const issues = [];
  let score = 100; // 기본 점수에서 감점 방식

  // 1. 길이 검증
  if (cleanText.length < MIN_MEANINGFUL_LENGTH) {
    issues.push('너무 짧은 피드백입니다.');
    score -= 30;
  }
  if (cleanText.length > MAX_FEEDBACK_LENGTH) {
    issues.push('피드백이 너무 깁니다.');
    score -= 20;
  }

  // 2. 금지 단어 검사
  const bannedWordCount = BANNED_WORDS.filter(word => 
    cleanText.includes(word)
  ).length;
  if (bannedWordCount > 0) {
    issues.push(`부적절한 표현이 포함되어 있습니다. (${bannedWordCount}개)`);
    score -= bannedWordCount * 25;
  }

  // 3. 스팸 패턴 검사
  const spamMatches = SPAM_PATTERNS.filter(pattern => 
    pattern.test(cleanText)
  ).length;
  if (spamMatches > 0) {
    issues.push(`스팸성 패턴이 감지되었습니다. (${spamMatches}개)`);
    score -= spamMatches * 15;
  }

  // 4. 반복 검사 (같은 단어 과도한 반복)
  const words = cleanText.split(/\s+/);
  const wordCount = {};
  words.forEach(word => {
    if (word.length > 1) {
      wordCount[word] = (wordCount[word] || 0) + 1;
    }
  });
  
  const maxWordRepeat = Math.max(...Object.values(wordCount));
  if (maxWordRepeat > 3) {
    issues.push('같은 단어의 과도한 반복이 있습니다.');
    score -= (maxWordRepeat - 3) * 10;
  }

  // 5. 의미 있는 내용 검사
  const meaningfulWords = words.filter(word => 
    word.length > 2 && !/^[ㅋㅎㅠㅜㅡ]+$/.test(word)
  );
  const meaningfulRatio = meaningfulWords.length / Math.max(words.length, 1);
  
  if (meaningfulRatio < 0.5) {
    issues.push('의미있는 내용이 부족합니다.');
    score -= 20;
  }

  // 6. 한글/영어 비율 검사 (무의미한 문자열 방지)
  const koreanChars = (cleanText.match(/[가-힣]/g) || []).length;
  const englishChars = (cleanText.match(/[a-zA-Z]/g) || []).length;
  const totalMeaningfulChars = koreanChars + englishChars;
  
  if (totalMeaningfulChars < cleanText.length * 0.7) {
    issues.push('무의미한 문자가 많이 포함되어 있습니다.');
    score -= 15;
  }

  // 점수 범위 조정 (0-100)
  score = Math.max(0, Math.min(100, score));

  // 카테고리 분류
  let category;
  if (score >= 80) category = 'excellent';
  else if (score >= 60) category = 'good';
  else if (score >= 40) category = 'moderate';
  else if (score >= 20) category = 'poor';
  else category = 'spam';

  return {
    score,
    issues,
    category,
    stats: {
      length: cleanText.length,
      wordCount: words.length,
      meaningfulRatio: Math.round(meaningfulRatio * 100),
      bannedWords: bannedWordCount,
      spamPatterns: spamMatches
    }
  };
}

/**
 * 피드백 승인 여부 결정
 * @param {string} text - 피드백 텍스트
 * @param {string} mood - 사용자 기분
 * @param {object} userInfo - 사용자 정보
 * @returns {object} 승인 결과
 */
export function shouldApproveFeedback(text, mood, userInfo = {}) {
  const qualityCheck = calculateQualityScore(text);
  
  // 기본 승인 기준
  const baseThreshold = 40;
  let threshold = baseThreshold;
  
  // 베타 사용자는 기준 완화
  if (userInfo.isBetaUser) {
    threshold -= 10;
  }
  
  // 로그인 사용자는 기준 완화
  if (userInfo.isAuthenticated) {
    threshold -= 5;
  }
  
  // 감정이 매우 부정적인 경우 더 엄격하게 검토
  if (mood === 'terrible') {
    threshold += 10;
  }
  
  const isApproved = qualityCheck.score >= threshold;
  
  return {
    approved: isApproved,
    score: qualityCheck.score,
    threshold,
    issues: qualityCheck.issues,
    category: qualityCheck.category,
    requiresReview: qualityCheck.score < 60 && qualityCheck.score >= threshold,
    stats: qualityCheck.stats
  };
}

/**
 * Rate Limiting 체크
 * @param {string} userIdentifier - 사용자 식별자 (IP, userId 등)
 * @param {number} maxSubmissions - 최대 제출 수
 * @param {number} timeWindowMs - 시간 윈도우 (밀리초)
 * @returns {object} Rate limiting 결과
 */
export function checkRateLimit(userIdentifier, maxSubmissions = 5, timeWindowMs = 60000) {
  const storageKey = `rateLimit_${userIdentifier}`;
  const now = Date.now();
  
  try {
    const submissions = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    // 시간 윈도우 밖의 제출은 제거
    const recentSubmissions = submissions.filter(timestamp => 
      now - timestamp < timeWindowMs
    );
    
    const isAllowed = recentSubmissions.length < maxSubmissions;
    
    if (isAllowed) {
      recentSubmissions.push(now);
      localStorage.setItem(storageKey, JSON.stringify(recentSubmissions));
    }
    
    return {
      allowed: isAllowed,
      remainingSubmissions: Math.max(0, maxSubmissions - recentSubmissions.length),
      resetTimeMs: recentSubmissions.length > 0 ? 
        Math.max(0, timeWindowMs - (now - Math.min(...recentSubmissions))) : 0,
      recentCount: recentSubmissions.length
    };
  } catch (error) {
    console.error('Rate limit check failed:', error);
    return { allowed: true, remainingSubmissions: maxSubmissions, resetTimeMs: 0, recentCount: 0 };
  }
}

/**
 * 피드백 데이터 정화 (개인정보 제거 등)
 * @param {string} text - 원본 텍스트
 * @returns {string} 정화된 텍스트
 */
export function sanitizeFeedback(text) {
  if (!text) return '';
  
  let sanitized = text.trim();
  
  // 전화번호 패턴 마스킹
  sanitized = sanitized.replace(/01[0-9]-?\d{4}-?\d{4}/g, '***-****-****');
  
  // 이메일 패턴 마스킹
  sanitized = sanitized.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '****@****.***');
  
  // 주민등록번호 패턴 마스킹
  sanitized = sanitized.replace(/\d{6}-?\d{7}/g, '******-*******');
  
  // 카드번호 패턴 마스킹  
  sanitized = sanitized.replace(/\d{4}-?\d{4}-?\d{4}-?\d{4}/g, '****-****-****-****');
  
  return sanitized;
}

/**
 * 피드백 자동 분류
 * @param {string} text - 피드백 텍스트
 * @param {string} mood - 사용자 기분
 * @returns {object} 분류 결과
 */
export function categorizeFeedback(text, mood) {
  const cleanText = text.toLowerCase();
  
  // 키워드 기반 분류
  const categories = {
    ui_ux: ['디자인', '인터페이스', '버튼', '색깔', '레이아웃', 'ui', 'ux', '화면'],
    performance: ['느려', '빨라', '속도', '로딩', '성능', '렉', '버벅'],
    feature: ['기능', '추가', '개선', '수정', '업데이트', '새로운'],
    bug: ['버그', '오류', '에러', '문제', '안됨', '작동', '고장'],
    content: ['문제', '질문', '답', '설명', '내용', '해설'],
    general: ['좋다', '나쁘다', '최고', '별로', '만족', '불만']
  };
  
  const scores = {};
  Object.entries(categories).forEach(([category, keywords]) => {
    scores[category] = keywords.filter(keyword => 
      cleanText.includes(keyword)
    ).length;
  });
  
  // 가장 높은 점수의 카테고리 선택
  const topCategory = Object.entries(scores)
    .sort(([,a], [,b]) => b - a)[0][0];
  
  return {
    category: topCategory,
    scores,
    confidence: Math.max(...Object.values(scores)) > 0 ? 'high' : 'low'
  };
}

/**
 * 종합 피드백 검증 및 처리
 * @param {object} feedbackData - 피드백 데이터
 * @param {object} userInfo - 사용자 정보
 * @returns {object} 검증 결과
 */
export function processFeedback(feedbackData, userInfo = {}) {
  const { text, mood, userAgent, page } = feedbackData;
  const userIdentifier = userInfo.userId || userAgent || 'anonymous';
  
  // 1. Rate Limiting 체크
  const rateLimit = checkRateLimit(userIdentifier);
  if (!rateLimit.allowed) {
    return {
      success: false,
      error: 'RATE_LIMITED',
      message: `너무 많은 피드백을 보내셨습니다. ${Math.ceil(rateLimit.resetTimeMs / 1000)}초 후에 다시 시도해주세요.`,
      resetTime: rateLimit.resetTimeMs
    };
  }
  
  // 2. 피드백 품질 검증
  const approval = shouldApproveFeedback(text, mood, userInfo);
  if (!approval.approved) {
    return {
      success: false,
      error: 'QUALITY_CHECK_FAILED',
      message: '피드백이 품질 기준을 충족하지 않습니다.',
      issues: approval.issues,
      score: approval.score
    };
  }
  
  // 3. 텍스트 정화
  const sanitizedText = sanitizeFeedback(text);
  
  // 4. 피드백 분류
  const classification = categorizeFeedback(sanitizedText, mood);
  
  // 5. 최종 처리된 피드백 데이터
  const processedFeedback = {
    ...feedbackData,
    text: sanitizedText,
    quality: {
      score: approval.score,
      category: approval.category,
      requiresReview: approval.requiresReview
    },
    classification,
    metadata: {
      userType: userInfo.isBetaUser ? 'beta' : userInfo.isAuthenticated ? 'user' : 'guest',
      processedAt: new Date().toISOString(),
      rateLimit: {
        remaining: rateLimit.remainingSubmissions,
        resetTime: rateLimit.resetTimeMs
      }
    }
  };
  
  return {
    success: true,
    feedback: processedFeedback,
    approval: approval
  };
}